/* Cart behaviour for sections/cart-drawer.liquid.

   Every mutation goes through the Cart API asking for this section back in the same
   call (bundled section rendering), and the reply is pasted over the drawer's body,
   its footer and its title. Nothing about a line — its total, whether the cart is now
   empty, which strip of products belongs under it — is recomputed here, so the drawer
   can never drift from what the server thinks the cart holds.

   assets/drawer.js owns opening and closing; this file only asks it to open. */
(function () {
  'use strict';

  var drawer = document.querySelector('[data-drawer="cart"]');
  if (!drawer) return;

  var sectionId = drawer.dataset.sectionId;

  function root() {
    /* Locale-aware, so a shopper on /fr stays on /fr. Always ends in a slash. */
    if (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) {
      return window.Shopify.routes.root;
    }
    return '/';
  }

  function loading(state) {
    drawer.classList.toggle('is-loading', state);
  }

  /* ------------------------------------------------------------------ rendering */

  function render(html) {
    var next = new DOMParser().parseFromString(html, 'text/html');

    ['[data-cart-body]', '[data-cart-foot]'].forEach(function (selector) {
      var incoming = next.querySelector(selector);
      var current = drawer.querySelector(selector);
      if (incoming && current) current.replaceWith(incoming);
    });

    /* The title carries the item count, and it lives in the shell's head rather than
       in either replaced region. Swapping the text keeps the close button — and the
       focus that may be sitting on it — untouched. */
    var incomingTitle = next.querySelector('.drawer__head .heading');
    var currentTitle = drawer.querySelector('.drawer__head .heading');
    if (incomingTitle && currentTitle) currentTitle.textContent = incomingTitle.textContent;
  }

  /* The badge is markup the icon snippet only prints when the count is above zero, so
     an emptied cart has to lose the element rather than be set to "0". */
  function setCount(count) {
    document.querySelectorAll('[data-cart-count]').forEach(function (holder) {
      var icon = holder.querySelector('.icon');
      if (!icon) return;

      icon.classList.toggle('has-number', count > 0);
      var badge = icon.querySelector('.icon-number');

      if (count > 0) {
        if (!badge) {
          badge = document.createElement('div');
          badge.className = 'icon-number';
          icon.appendChild(badge);
        }
        badge.textContent = count;
      } else if (badge) {
        badge.remove();
      }
    });
  }

  function showError(message) {
    var node = drawer.querySelector('[data-cart-error]');
    if (!node) return;
    node.textContent = message;
    node.hidden = !message;
  }

  function apply(data) {
    if (data.sections && data.sections[sectionId]) render(data.sections[sectionId]);
    if (typeof data.item_count === 'number') setCount(data.item_count);
  }

  /* ------------------------------------------------------------------ mutations */

  function post(path, body) {
    loading(true);
    return fetch(root() + path, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify(body)
    })
      .then(function (response) {
        return response.json();
      })
      .finally(function () {
        loading(false);
      });
  }

  function change(line, quantity) {
    if (!line) return;
    post('cart/change.js', { line: Number(line), quantity: quantity, sections: sectionId }).then(
      function (data) {
        /* change.js answers with the cart itself, so a reply carrying item_count is a
           success even when a quantity came back clamped to available stock. */
        if (typeof data.item_count !== 'number') {
          showError(data.description || data.message || '');
          return;
        }
        apply(data);
      }
    );
  }

  /* -------------------------------------------------------------------- binding */

  drawer.addEventListener('click', function (event) {
    var remove = event.target.closest('[data-cart-remove]');
    if (remove) {
      change(remove.dataset.line, 0);
      return;
    }

    var step = event.target.closest('[data-quantity-step]');
    if (!step) return;

    var selector = step.closest('[data-quantity-selector]');
    var input = selector && selector.querySelector('[data-quantity-input]');
    if (!input) return;

    /* Computed here rather than letting the stepper write to the field first: the
       field is about to be replaced by the server's answer, and a local value that
       briefly disagrees with it is what makes a quantity flicker. */
    var min = parseInt(input.min, 10);
    var max = parseInt(input.max, 10);
    var next = (parseInt(input.value, 10) || 0) + (parseInt(step.dataset.quantityStep, 10) || 0);

    if (!isNaN(min)) next = Math.max(min, next);
    if (!isNaN(max)) next = Math.min(max, next);

    change(selector.dataset.line, next);
  });

  drawer.addEventListener('change', function (event) {
    var input = event.target.closest('[data-quantity-input]');
    if (!input) return;

    var selector = input.closest('[data-quantity-selector]');
    if (!selector) return;

    var value = parseInt(input.value, 10);
    change(selector.dataset.line, isNaN(value) ? 0 : Math.max(0, value));
  });

  /* Add-to-cart anywhere on the page — the product page, the product-of-the-month
     card — opens the drawer instead of navigating to /cart. Delegated from the
     document so a form rendered later is covered too. */
  document.addEventListener('submit', function (event) {
    var form = event.target.closest('form[action*="/cart/add"]');
    if (!form) return;

    event.preventDefault();

    var body = new FormData(form);
    body.append('sections', sectionId);

    loading(true);
    fetch(root() + 'cart/add.js', { method: 'POST', headers: { Accept: 'application/json' }, body: body })
      .then(function (response) {
        return response.json().then(function (data) {
          return { ok: response.ok, data: data };
        });
      })
      .then(function (result) {
        if (window.themeDrawer) window.themeDrawer.open('cart');

        if (!result.ok) {
          /* A sold-out or over-stock add. The cart did not change, so nothing is
             re-rendered — only the reason is shown. */
          showError(result.data.description || result.data.message || '');
          return;
        }

        apply(result.data);

        /* add.js reports what was added, not the cart, so the badge is asked for
           separately rather than inferred from the line just posted. */
        fetch(root() + 'cart.js', { headers: { Accept: 'application/json' } })
          .then(function (response) {
            return response.json();
          })
          .then(function (cart) {
            setCount(cart.item_count);
          });
      })
      .catch(function () {
        if (window.themeDrawer) window.themeDrawer.open('cart');
      })
      .finally(function () {
        loading(false);
      });
  });
})();
